// Copyright (C) 2026, Lifefinder Systems International AB, all rights reserved.
import React, { useMemo, useState } from 'react';

import { useDevices } from '../context/DeviceContext';
import { activeColor, alarmColor } from '../styles/constants';
import type { Device, LatLng } from '../types';
import { formatRelativeTime } from '../utils/dateUtils';
import { showAsActive, showAsAlarm } from '../utils/deviceUtils';

export interface DeviceStatusBoxesProps {
  /** Fly the map to a device's last known position and select it. */
  onDeviceClick: (deviceId: string, latlng: LatLng) => void;
}

type BoxKind = 'active' | 'alarm';

const boxBase: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.6em',
  padding: '0.5em 0.9em',
  borderRadius: '6px',
  border: 'none',
  cursor: 'pointer',
  fontSize: 'max(12px, 1.8vmin)',
  fontWeight: 700,
  color: '#fff',
  boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
};

const countStyle: React.CSSProperties = {
  fontSize: '1.3em',
  lineHeight: 1,
};

/** Neutral grey when the count is 0, the marker color otherwise. */
const boxColor = (kind: BoxKind, count: number): string => {
  if (count === 0) return '#9e9e9eee';
  return kind === 'alarm' ? alarmColor : activeColor;
};

const hasPosition = (d: Device): boolean =>
  Array.isArray(d.latlng) &&
  d.latlng.length === 2 &&
  Number.isFinite(d.latlng[0]) &&
  Number.isFinite(d.latlng[1]);

const formatPosition = (d: Device): string => {
  // A geofenced position resolves to a named area — show the name, not raw coordinates.
  if (d.positionType === 'GEOFENCE' && d.area) return d.area;
  return hasPosition(d)
    ? `${d.latlng![0].toFixed(5)}, ${d.latlng![1].toFixed(5)}`
    : 'no known position';
};

/**
 * DeviceStatusBoxes — timespace-only overlay with two count boxes:
 * devices currently flagged active (output.deviceActive=1, the green markers)
 * and devices currently alarming (the red markers). Clicking a box pops out
 * the matching device list with each device's last known position and how long
 * ago it was solved; clicking a row centers the map there and selects the device.
 *
 * Counts come from filteredDevices (the full truth), not the rendered marker
 * set, so a device in alarm without a solvable position is still counted — its
 * row is shown unclickable instead of being silently dropped.
 */
export const DeviceStatusBoxes: React.FC<DeviceStatusBoxesProps> = ({ onDeviceClick }) => {
  const { filteredDevices } = useDevices();
  const [openList, setOpenList] = useState<BoxKind | null>(null);

  const activeDevices = useMemo(() => filteredDevices.filter(showAsActive), [filteredDevices]);
  const alarmDevices = useMemo(() => filteredDevices.filter(showAsAlarm), [filteredDevices]);

  const toggleList = (kind: BoxKind): void => setOpenList((cur) => (cur === kind ? null : kind));

  const listFor = openList === 'alarm' ? alarmDevices : activeDevices;

  // Positioned by the parent (top-centre strip in Map.tsx, next to the
  // checkin summaries); the popout list opens downward below the boxes.
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.6vmin',
        alignItems: 'center',
      }}
    >
      <div style={{ display: 'flex', gap: '0.6vmin' }}>
        <button
          data-testid="device-status-box-active"
          style={{ ...boxBase, background: boxColor('active', activeDevices.length) }}
          onClick={() => toggleList('active')}
          title="Devices currently flagged active — click for the list"
        >
          <span style={countStyle}>{activeDevices.length}</span> Active
        </button>
        <button
          data-testid="device-status-box-alarm"
          style={{ ...boxBase, background: boxColor('alarm', alarmDevices.length) }}
          onClick={() => toggleList('alarm')}
          title="Devices currently alarming — click for the list"
        >
          <span style={countStyle}>{alarmDevices.length}</span> Alarming
        </button>
      </div>

      {openList && (
        <div
          data-testid="device-status-list"
          style={{
            background: '#fff',
            borderRadius: '6px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.35)',
            padding: '0.6em',
            maxHeight: '40vh',
            maxWidth: 'min(90vw, 28em)',
            overflowY: 'auto',
            fontSize: 'max(12px, 1.6vmin)',
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: '0.4em', color: '#444' }}>
            {openList === 'alarm' ? 'Alarming devices' : 'Active devices'} ({listFor.length})
          </div>
          {listFor.length === 0 && <div style={{ color: '#888' }}>None right now.</div>}
          {listFor.map((d) => {
            const clickable = hasPosition(d);
            return (
              <button
                key={d.id}
                data-testid={`device-status-row-${d.id}`}
                disabled={!clickable}
                onClick={() => clickable && onDeviceClick(d.id, d.latlng as LatLng)}
                title={clickable ? 'Center the map on this position' : 'No position to center on'}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  background: 'transparent',
                  border: 'none',
                  borderTop: '1px solid #eee',
                  padding: '0.4em 0.2em',
                  cursor: clickable ? 'pointer' : 'default',
                  opacity: clickable ? 1 : 0.5,
                }}
              >
                <div style={{ fontWeight: 600, color: '#222' }}>{d.title || d.id}</div>
                <div style={{ color: '#666', fontSize: '0.9em' }}>
                  {formatPosition(d)}
                  {d.positionTime != null && <> · {formatRelativeTime(d.positionTime)}</>}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DeviceStatusBoxes;
